// ─────────────────────────────────────────────────────────────────────
// WHICH CHANGE AM I LOOKING AT — the commit this bundle was built from.
//
// `__VITE_BUILD_ID__` already exists and answers a different question: «are you
// still the build I am?», which is a comparison against `build.json` and not
// something anybody reads. On a dev deployment the question people actually ask
// is which change is live, and the answer is the commit — its subject carries
// the squash-merge's PR number, and its date is when the change LANDED rather
// than when the bundle happened to be built.
//
// Injected by `vite.config.ts` at build time. Absent is a NORMAL state: a build
// from a tarball or a Docker layer with no `.git` has nothing to say, and so
// does a `define` that was never replaced. Both come out as `null` here so the
// caller renders «unknown» rather than an empty box that looks broken.
// ─────────────────────────────────────────────────────────────────────

declare global {
  interface Window {
    __VITE_BUILD_SHA__?: string;
    __VITE_BUILD_SUBJECT__?: string;
    __VITE_BUILD_AT__?: string;
  }
}

export interface BuildCommit {
  sha: string;
  subject: string;
  /** ISO-8601 with offset, as `git log --pretty=%cI` gives it. */
  at: string;
  /**
   * The pull request the subject names, when it names one.
   *
   * Squash merges end in `(#598)`, which is the number worth showing — so the
   * LAST match wins. A commit message may mention other issues in passing
   * («closes #123»), and the first number in the line is as likely to be one of
   * those as the PR itself.
   */
  pr: number | null;
}

/** The last `(#123)` in a commit subject, which is where a squash merge puts it. */
export function prFromSubject(subject: string): number | null {
  const matches = [...(subject || '').matchAll(/\(#(\d+)\)/g)];
  const last = matches.at(-1);
  return last ? Number(last[1]) : null;
}

/** What this bundle was built from, or `null` when nothing was injected. */
export function buildCommit(): BuildCommit | null {
  try {
    const sha = window.__VITE_BUILD_SHA__;
    const subject = window.__VITE_BUILD_SUBJECT__;
    const at = window.__VITE_BUILD_AT__;
    // A `define` that never ran leaves the literal token, not a string — and a
    // build without git leaves ''. Both mean «nothing to say».
    if (typeof sha !== 'string' || !sha) return null;
    return {
      sha,
      subject: typeof subject === 'string' ? subject : '',
      at: typeof at === 'string' ? at : '',
      pr: prFromSubject(typeof subject === 'string' ? subject : ''),
    };
  } catch {
    return null;
  }
}

/**
 * «30. sep. 2026, 14:05» — in the reader's locale, from the commit's own offset.
 *
 * DISPLAY ONLY (DOMAIN_RULES §4): this never becomes a stored value or a key.
 * An unparseable date returns the raw string rather than «Invalid Date», which
 * at least shows what was there.
 */
export function formatBuildTime(at: string, locale = 'nb-NO'): string {
  if (!at) return '';
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return at;
  return d.toLocaleString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
